import type { Dict } from "../types";

export const giftCards: Dict = {
  en: {

    // Gift Cards
    "GiftCards.title": "Gift Cards",
    "GiftCards.cardCode": "Card Code",
    "GiftCards.legacyNumber": "Former number",
    "GiftCards.status": "Status",
    "GiftCards.primaryCustomer": "Client",
    "GiftCards.secondaryCustomers": "Other Client",
    "GiftCards.issueDate": "Issue Date",
    "GiftCards.issueDateOptionalHint": "Optional. Leave empty to use today's date.",
    "GiftCards.addImportedCard": "Add old gift card",
    "GiftCards.importDialogTitle": "Add an old gift card",
    "GiftCards.importDialogDescription": "Add a gift card sold before using the software. It is never counted as a sale: no cash movement, not in revenue. Its balance stays usable.",
    "GiftCards.importButton": "Add gift card",
    "GiftCards.importSuccess": "Old gift card added",
    "GiftCards.importError": "Could not add the gift card",
    "GiftCards.imported": "Imported",
    "GiftCards.associateClients": "Associated clients",
    "GiftCards.importClientHint": "At least one client is required. The first selected becomes the primary holder.",
    "GiftCards.primaryHolder": "primary",
    "GiftCards.setPrimaryHolder": "Set as primary holder",
    "GiftCards.noCustomersFound": "No client found",
    "GiftCards.showingResultsSingular": "Showing {start} to {end} of {total} gift card",
    "GiftCards.amountRequired": "Enter an amount greater than zero.",
    "GiftCards.customerRequired": "Select at least one client.",
    "GiftCards.issueDateInFuture": "The issue date cannot be in the future.",
    "GiftCards.expirationBeforeIssue": "The expiration date cannot be earlier than the issue date.",
    "GiftCards.selectedCustomers": "Selected: {names}",
    "GiftCards.expiredCardWarning": "This card has expired. The client you link will not be able to use it.",
    "GiftCards.emptyCardWarning": "This card is empty. The client you link will get nothing from it.",
    "GiftCards.cancelledCardBlocked": "This card is cancelled. You cannot link a client to it.",
    "GiftCards.noCardSelected": "Select a gift card first.",
    "GiftCards.noChangeToSave": "No change to save.",
    "GiftCards.expirationDate": "Expiration Date",
    "GiftCards.initialAmount": "Initial Amount",
    "GiftCards.currentBalance": "Current Balance",
    "GiftCards.lastUsedAt": "Last Use Date",
    "GiftCards.active": "Active",
    "GiftCards.used": "Used",
    "GiftCards.expired": "Expired",
    "GiftCards.cancelled": "Cancelled",
    "GiftCards.deleted": "Deleted",
    "GiftCards.deletedOnBy": "Deleted on {date} by {name}",
    "GiftCards.deletedOn": "Deleted on {date}",
    "GiftCards.restoredOnBy": "back in circulation on {date}, by {name}",
    "GiftCards.restoredOn": "back in circulation on {date}",
    "GiftCards.noCards": "No gift cards yet",
    "GiftCards.createFirst": "Create your first gift card to get started.",
    "GiftCards.addNew": "Add New Gift Card",
    "GiftCards.noCustomer": "No customer",
    "GiftCards.never": "Never",
    "GiftCards.empty": "Empty",
    "GiftCards.filterByCode": "Filter by Code",
    "GiftCards.searchCode": "Search by code...",
    "GiftCards.filterByStatus": "Filter by Status",
    "GiftCards.filterByCustomer": "Filter by Customer",
    "GiftCards.searchCustomer": "Search for a customer...",
    "GiftCards.filterByIssueDate": "Filter by Issue Date",
    "GiftCards.filterByExpirationDate": "Filter by Expiration Date",
    "GiftCards.showingResults": "Showing {start} to {end} of {total} gift cards",
    "GiftCards.associateCustomers": "Associate Customers",
    "GiftCards.associateCustomersDescription": "Add customer accounts to a gift card so they can use it.",
    "GiftCards.selectGiftCard": "Select a gift card",
    "GiftCards.selectCustomers": "Select customers to add",
    "GiftCards.searchGiftCard": "Search gift card by code...",
    "GiftCards.associateButton": "Associate",
    "GiftCards.associateSuccess": "Customers associated successfully",
    "GiftCards.associateError": "Failed to associate customers",
    "GiftCards.alreadyAssociated": "Already associated",
    "GiftCards.removeSecondary": "Remove",
    "GiftCards.currentSecondaryCustomers": "Current secondary customers",
    "GiftCards.noSecondaryCustomers": "No secondary customers",
    "GiftCards.actions": "Actions",
    "GiftCards.deleteTitle": "Delete Gift Card",
    "GiftCards.deleteConfirm": "This card still holds {balance}. It will immediately stop being redeemable and will leave the list. Its transaction history is kept, and you can restore it from the Status filter, \"Deleted\" checkbox.",
    "GiftCards.deleteSuccess": "Gift card deleted successfully",
    "GiftCards.deleteError": "Failed to delete gift card",
    "GiftCards.restore": "Restore",
    "GiftCards.restoreTitle": "Restore gift card",
    "GiftCards.restoreConfirm": "This card will become redeemable again, with its balance of {balance}, and will go back into the list.",
    "GiftCards.restoreSuccess": "Gift card restored successfully",
    "GiftCards.restoreError": "Failed to restore gift card",
    "GiftCards.viewItems": "View Items",
    "GiftCards.itemName": "Item",
    "GiftCards.quantity": "Qty",
    "GiftCards.redeemed": "Redeemed",
    "GiftCards.unitValue": "Value",
    "GiftCards.cardCount": "{count} gift card",
    "GiftCards.cardCountPlural": "{count} gift cards",
    "GiftCards.rechargeHint": "To recharge or add a gift card, use the invoicing module.",
    "GiftCards.noCardsDescription": "Gift cards are created through invoicing.",
    "GiftCards.noMatchingFilters": "No gift cards match the applied filters.",
    "GiftCards.show": "Show",
    "GiftCards.perPage": "per page",
    "GiftCards.pageOf": "Page {current} of {total}",
    "GiftCards.cancel": "Cancel",
    "GiftCards.delete": "Delete",
    "GiftCards.saving": "Saving...",
    "GiftCards.additionalNotes": "Additional notes...",
    "GiftCards.noAccessTitle": "No access",
    "GiftCards.noAccessDescription": "You do not have permission to view gift cards. Contact the salon owner to request access.",
    "GiftCards.linkClientsPermissionRequired": "Permission required to link clients to gift cards.",
    "GiftCards.deleteCardsPermissionRequired": "Permission required to delete or restore gift cards.",

    // Gift cards — unified header
    "GiftCards.tileActive": "Active cards",
    "GiftCards.tileOutstanding": "Outstanding balance",
    "GiftCards.tileIssuedThisMonth": "Issued this month",
    "GiftCards.tileExpiringSoon": "Expiring soon",
  },
  fr: {

    // Gift Cards
    "GiftCards.title": "Cartes cadeaux",
    "GiftCards.cardCode": "Code de la carte",
    "GiftCards.legacyNumber": "Ancien numéro",
    "GiftCards.status": "Statut",
    "GiftCards.primaryCustomer": "Client",
    "GiftCards.secondaryCustomers": "Autre client",
    "GiftCards.issueDate": "Date d'émission",
    "GiftCards.issueDateOptionalHint": "Optionnel. Laisser vide pour utiliser la date du jour.",
    "GiftCards.addImportedCard": "Ajouter ancienne carte cadeau",
    "GiftCards.importDialogTitle": "Ajouter une ancienne carte cadeau",
    "GiftCards.importDialogDescription": "Ajoutez une carte cadeau vendue avant l'utilisation du logiciel. Elle n'est jamais comptée comme une vente : aucun mouvement de caisse, exclue du chiffre d'affaires. Son solde reste utilisable.",
    "GiftCards.importButton": "Ajouter la carte",
    "GiftCards.importSuccess": "Ancienne carte cadeau ajoutée",
    "GiftCards.importError": "Impossible d'ajouter la carte cadeau",
    "GiftCards.imported": "Importée",
    "GiftCards.associateClients": "Clients associés",
    "GiftCards.importClientHint": "Au moins un client est requis. Le premier sélectionné devient le détenteur principal.",
    "GiftCards.primaryHolder": "principal",
    "GiftCards.setPrimaryHolder": "Définir comme titulaire principal",
    "GiftCards.noCustomersFound": "Aucun client trouvé",
    "GiftCards.showingResultsSingular": "Affichage de {start} à {end} sur {total} carte cadeau",
    "GiftCards.amountRequired": "Saisissez un montant supérieur à zéro.",
    "GiftCards.customerRequired": "Sélectionnez au moins un client.",
    "GiftCards.issueDateInFuture": "La date d'émission ne peut pas être dans le futur.",
    "GiftCards.expirationBeforeIssue": "La date d'expiration ne peut pas être antérieure à la date d'émission.",
    "GiftCards.selectedCustomers": "Sélectionnés : {names}",
    "GiftCards.expiredCardWarning": "Cette carte est expirée. Le client que vous associez ne pourra pas l'utiliser.",
    "GiftCards.emptyCardWarning": "Cette carte est vide. Le client que vous associez n'en tirera rien.",
    "GiftCards.cancelledCardBlocked": "Cette carte est annulée. Vous ne pouvez pas y associer de client.",
    "GiftCards.noCardSelected": "Choisissez d'abord une carte cadeau.",
    "GiftCards.noChangeToSave": "Aucun changement à enregistrer.",
    "GiftCards.expirationDate": "Date d'expiration",
    "GiftCards.initialAmount": "Montant initial",
    "GiftCards.currentBalance": "Solde",
    "GiftCards.lastUsedAt": "Date de la dernière utilisation",
    "GiftCards.active": "Active",
    "GiftCards.used": "Utilisée",
    "GiftCards.expired": "Expirée",
    "GiftCards.cancelled": "Annulée",
    "GiftCards.deleted": "Supprimée",
    "GiftCards.deletedOnBy": "Supprimée le {date} par {name}",
    "GiftCards.deletedOn": "Supprimée le {date}",
    "GiftCards.restoredOnBy": "remise en circulation le {date}, par {name}",
    "GiftCards.restoredOn": "remise en circulation le {date}",
    "GiftCards.noCards": "Aucune carte cadeau",
    "GiftCards.createFirst": "Créez votre première carte cadeau pour commencer.",
    "GiftCards.addNew": "Ajouter une carte cadeau",
    "GiftCards.noCustomer": "Aucun client",
    "GiftCards.never": "Jamais",
    "GiftCards.empty": "Vide",
    "GiftCards.filterByCode": "Filtrer par code",
    "GiftCards.searchCode": "Rechercher par code...",
    "GiftCards.filterByStatus": "Filtrer par statut",
    "GiftCards.filterByCustomer": "Filtrer par client",
    "GiftCards.searchCustomer": "Rechercher un client...",
    "GiftCards.filterByIssueDate": "Filtrer par date d'émission",
    "GiftCards.filterByExpirationDate": "Filtrer par date d'expiration",
    "GiftCards.showingResults": "Affichage de {start} à {end} sur {total} cartes cadeaux",
    "GiftCards.associateCustomers": "Associer des clients",
    "GiftCards.associateCustomersDescription": "Ajoutez des comptes clients à une carte cadeau pour qu'ils puissent l'utiliser.",
    "GiftCards.selectGiftCard": "Sélectionner une carte cadeau",
    "GiftCards.selectCustomers": "Sélectionner les clients à ajouter",
    "GiftCards.searchGiftCard": "Rechercher une carte cadeau par code...",
    "GiftCards.associateButton": "Associer",
    "GiftCards.associateSuccess": "Clients associés avec succès",
    "GiftCards.associateError": "Échec de l'association des clients",
    "GiftCards.alreadyAssociated": "Déjà associé",
    "GiftCards.removeSecondary": "Retirer",
    "GiftCards.currentSecondaryCustomers": "Clients secondaires actuels",
    "GiftCards.noSecondaryCustomers": "Aucun client secondaire",
    "GiftCards.actions": "Actions",
    "GiftCards.deleteTitle": "Supprimer la carte cadeau",
    "GiftCards.deleteConfirm": "Cette carte porte encore {balance}. Elle cessera immédiatement d'être encaissable et sortira de la liste. Son historique de transactions est conservé, et vous pourrez la restaurer via le filtre Statut, case « Supprimée ».",
    "GiftCards.deleteSuccess": "Carte cadeau supprimée avec succès",
    "GiftCards.deleteError": "Échec de la suppression de la carte cadeau",
    "GiftCards.restore": "Restaurer",
    "GiftCards.restoreTitle": "Restaurer la carte cadeau",
    "GiftCards.restoreConfirm": "Cette carte redeviendra encaissable, avec son solde de {balance}, et réapparaîtra dans la liste.",
    "GiftCards.restoreSuccess": "Carte cadeau restaurée avec succès",
    "GiftCards.restoreError": "Échec de la restauration de la carte cadeau",
    "GiftCards.viewItems": "Voir les articles",
    "GiftCards.itemName": "Article",
    "GiftCards.quantity": "Qté",
    "GiftCards.redeemed": "Utilisé(s)",
    "GiftCards.unitValue": "Valeur",
    "GiftCards.cardCount": "{count} carte cadeau",
    "GiftCards.cardCountPlural": "{count} cartes cadeaux",
    "GiftCards.rechargeHint": "Pour recharger ou ajouter une carte cadeau, utilisez le module de facturation.",
    "GiftCards.noCardsDescription": "Les cartes cadeaux sont créées via la facturation.",
    "GiftCards.noMatchingFilters": "Aucune carte cadeau ne correspond aux filtres appliqués.",
    "GiftCards.show": "Afficher",
    "GiftCards.perPage": "par page",
    "GiftCards.pageOf": "Page {current} sur {total}",
    "GiftCards.cancel": "Annuler",
    "GiftCards.delete": "Supprimer",
    "GiftCards.saving": "Enregistrement...",
    "GiftCards.additionalNotes": "Notes additionnelles...",
    "GiftCards.noAccessTitle": "Accès refusé",
    "GiftCards.noAccessDescription": "Vous n'avez pas la permission de voir les cartes cadeaux. Contactez le propriétaire du salon pour demander l'accès.",
    "GiftCards.linkClientsPermissionRequired": "Permission requise pour associer des clients aux cartes cadeaux.",
    "GiftCards.deleteCardsPermissionRequired": "Permission requise pour supprimer ou restaurer des cartes cadeaux.",

    // Cartes cadeaux — en-tête unifié
    "GiftCards.tileActive": "Cartes actives",
    "GiftCards.tileOutstanding": "Solde en circulation",
    "GiftCards.tileIssuedThisMonth": "Émises ce mois",
    "GiftCards.tileExpiringSoon": "Expirent bientôt",
  },
};
